import { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import useDocumentMeta from '@/hooks/useDocumentMeta';
import Breadcrumb from '@/components/feature/Breadcrumb';
import BlacklistCard from '@/components/feature/BlacklistCard';
import StateBlock from '@/components/base/StateBlock';
import BlacklistFilter, { type FilterState } from './components/BlacklistFilter';
import { filterBlacklist } from '@/utils/content';

const PAGE_SIZE = 3;

const defaultFilter: FilterState = {
  event: 'all',
  dateFrom: '',
  dateTo: '',
  rankFrom: 1,
  rankTo: 20,
};

export default function BlacklistCategory() {
  const [searchParams] = useSearchParams();
  const initialEvent = searchParams.get('event') || 'all';
  const [filter, setFilter] = useState<FilterState>({ ...defaultFilter, event: initialEvent });
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [sheetOpen, setSheetOpen] = useState(false);

  useDocumentMeta({
    title: '黑榜列表 - 51每日大赛',
    description:
      '51每日大赛黑榜列表，按赛事名称、日期范围与排名区间筛选，查看各大赛事最新黑榜名单、排名变化与选手表现指数。',
    keywords: '51每日大赛,黑榜列表,今日黑榜,赛事排名,选手表现,黑榜筛选',
  });

  useEffect(() => {
    if (initialEvent !== 'all') {
      setFilter((prev) => ({ ...prev, event: initialEvent }));
    }
  }, [initialEvent]);

  const results = useMemo(() => filterBlacklist(filter), [filter]);
  const shown = results.slice(0, visible);

  const handleChange = (next: FilterState) => {
    setFilter(next);
    setVisible(PAGE_SIZE);
  };
  const handleReset = () => {
    setFilter({ ...defaultFilter });
    setVisible(PAGE_SIZE);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
      <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '赛事黑榜' }]} />

      <header className="mt-5">
        <h1 className="text-2xl md:text-3xl font-black text-foreground-950 tracking-tight">
          赛事黑榜列表
        </h1>
        <p className="mt-3 max-w-3xl text-sm md:text-base text-foreground-600 leading-relaxed">
          汇总各大赛事每日黑榜名单，支持按赛事、日期与排名区间多维筛选，帮助你快速定位排名下滑明显、表现指数偏低的选手。
          <span className="ml-1 text-foreground-400">演示内容，实际数据接入待开发。</span>
        </p>
      </header>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6">
        <aside className="hidden lg:block">
          <div className="sticky top-24 rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="text-base font-bold text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-filter-3-line text-primary-600"></i>
              筛选条件
            </h2>
            <BlacklistFilter
              value={filter}
              onChange={handleChange}
              onReset={handleReset}
              resultCount={results.length}
            />
          </div>
        </aside>

        <div>
          <button
            type="button"
            onClick={() => setSheetOpen(true)}
            className="lg:hidden w-full mb-4 px-4 py-3 rounded-md bg-primary-600 text-background-50 text-sm font-medium flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-filter-3-line"></i>
            筛选条件（{results.length} 条结果）
          </button>

          {shown.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {shown.map((item) => (
                  <BlacklistCard key={item.id} item={item} />
                ))}
              </div>
              {visible < results.length && (
                <div className="mt-8 flex justify-center">
                  <button
                    type="button"
                    onClick={() => setVisible((v) => v + PAGE_SIZE)}
                    className="px-6 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm font-medium text-foreground-800 hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    加载更多
                    <i className="ri-arrow-down-line align-middle ml-1"></i>
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="rounded-lg border border-background-200 bg-background-50">
              <StateBlock
                type="empty"
                title="无相关黑榜数据，尝试调整筛选条件"
                description="可以放宽日期范围或排名区间，也可以切换其他赛事查看。"
                actionText="重置筛选"
                onAction={handleReset}
              />
            </div>
          )}
        </div>
      </div>

      {sheetOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex items-end">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setSheetOpen(false)}
            className="absolute inset-0 bg-primary-950/50"
          ></button>
          <div className="relative w-full bg-background-50 rounded-t-2xl p-5 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-foreground-950">筛选条件</h2>
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                aria-label="关闭"
                className="w-9 h-9 flex items-center justify-center rounded-md hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <BlacklistFilter
              value={filter}
              onChange={handleChange}
              onReset={handleReset}
              resultCount={results.length}
            />
            <button
              type="button"
              onClick={() => setSheetOpen(false)}
              className="mt-5 w-full py-3 rounded-md bg-primary-600 text-background-50 text-sm font-bold cursor-pointer whitespace-nowrap"
            >
              查看 {results.length} 条结果
            </button>
          </div>
        </div>
      )}
    </div>
  );
}